import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesMovies, chunkArraySeries } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

const leftoverTiles = new Map<string, any[]>();

// Cache for processed Last Added movies
const lastAddedCache = new Map<
  string,
  {
    data: any[];
    timestamp: number;
    cacheKey: string;
    version: number;
  }
>();

// Cache expiry time (24 hours in milliseconds)
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;
const CACHE_VERSION = 1;

// Enhanced fetchData wrapper with JSON error handling
const safeFetchData = async (url, context = "unknown") => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      console.log(`📡 Safe fetch attempt ${attempt}/${MAX_RETRIES} for ${context}: ${url}`);

      const result = await fetchData(url);

      // Validate result is not HTML
      if (typeof result === "string" && result.trim().startsWith("<")) {
        throw new Error(`Received HTML instead of JSON for ${context}`);
      }

      // Ensure result is an array
      if (!Array.isArray(result)) {
        if (result && typeof result === "object") {
          // Try to extract array from object
          if (Array.isArray(result.data)) return result.data;
          if (Array.isArray(result.categories)) return result.categories;
          if (Array.isArray(result.streams)) return result.streams;
          if (Array.isArray(result.movies)) return result.movies;
        }
        console.warn(`Non-array result for ${context}:`, typeof result);
        return [];
      }

      console.log(`✅ Safe fetch successful for ${context}: ${result.length} items`);
      return result;
    } catch (error) {
      console.warn(`⚠️ Safe fetch attempt ${attempt} failed for ${context}:`, error.message);

      if (
        error.message.includes("Unexpected token <") ||
        error.message.includes("JSON") ||
        error.message.includes("HTML instead of JSON")
      ) {
        console.error(`❌ JSON parsing error for ${context}`);
      }

      if (attempt === MAX_RETRIES) {
        console.error(`❌ All safe fetch attempts failed for ${context}, returning empty array`);
        return [];
      }

      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    }
  }

  return [];
};

// Helper function to build API URL with DNS support and enhanced error handling
const buildApiUrl = async (
  baseUrl: string,
  username: string,
  password: string,
  action: string,
  additionalParams: string = "",
): Promise<string> => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;

  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  // CUSTOM DNS TRANSFORMATION with error handling
  let finalApiUrl = apiUrl;
  try {
    console.log(`🌐 Starting DNS transformation for Last Added API (${action}): ${apiUrl}`);

    const dnsResult = await dnsResolver.transformUrl(apiUrl);

    console.log(`📡 DNS transformation result for Last Added (${action}):`, dnsResult);

    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
      console.log(`✅ Custom DNS applied to Last Added (${action}): ${apiUrl} → ${finalApiUrl}`);
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
      console.log(`🔄 Last Added URL modified (${action}): ${finalApiUrl}`);
    } else {
      console.log(`📝 Last Added URL unchanged (${action}) - localhost or DNS disabled`);
    }
  } catch (dnsError) {
    console.warn(`❌ DNS transformation failed for Last Added (${action}):`, dnsError);
    finalApiUrl = apiUrl;
  }

  console.log(`📞 Last Added API URL (${action}): ${finalApiUrl}`);
  return finalApiUrl;
};

export default function (filter: string) {
  return async (pageIndex: number): Promise<any> => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    // Validate required parameters
    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Last Added movies");
      return [];
    }

    console.log("playListUrl():", playListUrl());
    console.log("DNS Info:", window.dnsResolver?.getDnsInfo());

    // FIX: Make tileKey unique per playlist to prevent leftover tiles mixing
    const tileKey = `${baseUrl}_${username}_${password}_${filter}_vod_last_added`;
    const cacheKey = `${baseUrl}_${username}_${password}_${filter}_last_added`;

    // Check if we have valid cached data
    const cachedData = lastAddedCache.get(cacheKey);
    const now = Date.now();

    if (cachedData && now - cachedData.timestamp < CACHE_EXPIRY && cachedData.version === CACHE_VERSION) {
      console.log("🚀 Using cached Last Added movies data");

      const existingTiles = leftoverTiles.get(tileKey) || [];
      const tiles = existingTiles.concat(cachedData.data);

      const chunks = chunkArraySeries(tiles);
      if (chunks[chunks?.length - 1]?.length < 7) {
        leftoverTiles.set(tileKey, chunks.pop() || []);
      } else {
        leftoverTiles.delete(tileKey);
      }

      return tiles;
    }

    // ENHANCED: Fetch with better error handling
    try {
      console.log("📡 Fetching Last Added movies from first 5 categories with enhanced error handling...");

      // Step 1: Get movie categories with enhanced error handling
      const categoriesUrl = await buildApiUrl(baseUrl, username, password, "get_vod_categories");
      const categories = await safeFetchData(categoriesUrl, "categories");

      if (!Array.isArray(categories) || categories.length === 0) {
        console.warn("No movie categories available for Last Added");
        return [];
      }

      console.log(`📊 Found ${categories.length} categories, using first 5 for Last Added`);

      // Step 2: Get first 5 categories
      const first5Categories = categories.slice(0, 5);
      let allRecentMovies: any[] = [];

      // Step 3: Fetch movies from each category with enhanced error handling
      for (const category of first5Categories) {
        try {
          const categoryId = category.category_id;
          const categoryName = category.category_name;

          console.log(`🎬 Fetching movies from category: "${categoryName}" (ID: ${categoryId})`);

          const categoryMoviesUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_vod_streams",
            `category_id=${categoryId}`,
          );

          const categoryMovies = await safeFetchData(categoryMoviesUrl, `category ${categoryName}`);

          if (Array.isArray(categoryMovies) && categoryMovies.length > 0) {
            const moviesWithCategory = categoryMovies.map(movie => ({
              ...movie,
              source_category: categoryName,
            }));

            allRecentMovies.push(...moviesWithCategory);
            console.log(`✅ Added ${categoryMovies.length} movies from "${categoryName}"`);
          } else {
            console.log(`📦 No movies found in category "${categoryName}"`);
          }
        } catch (error) {
          console.warn(`Failed to fetch category ${category.category_name}:`, error.message);
          // Continue with other categories instead of failing completely
        }
      }

      console.log(`📦 Total movies collected from 5 categories: ${allRecentMovies.length}`);

      // Step 4: Process and filter movies
      const seenNames = new Set<string>();
      const uniqueMovies = allRecentMovies
        .filter((movie: any) => movie && movie.added && movie.name)
        .filter((movie: any) => {
          const movieName = movie.name.toLowerCase().trim();
          if (seenNames.has(movieName)) {
            return false;
          }
          seenNames.add(movieName);
          return true;
        });

      // Step 5: Sort and limit
      const sortedMovies = uniqueMovies
        .sort((a: any, b: any) => {
          const addedA = parseInt(a.added || "0");
          const addedB = parseInt(b.added || "0");
          return addedB - addedA;
        })
        .slice(0, 20);

      console.log(
        `✅ Last Added movies processed: ${uniqueMovies.length} unique → ${sortedMovies.length} newest`,
      );

      // Step 6: Convert to tiles
      const processedTiles = convertItemsToTilesMovies(sortedMovies);

      // Step 7: Cache the results
      lastAddedCache.set(cacheKey, {
        data: processedTiles,
        timestamp: now,
        cacheKey: cacheKey,
        version: CACHE_VERSION,
      });

      console.log("💾 Last Added movies cached successfully");

      // Step 8: Return tiles
      const existingTiles = leftoverTiles.get(tileKey) || [];
      const tiles = existingTiles.concat(processedTiles);

      const chunks = chunkArraySeries(tiles);
      if (chunks[chunks?.length - 1]?.length < 7) {
        leftoverTiles.set(tileKey, chunks.pop() || []);
      } else {
        leftoverTiles.delete(tileKey);
      }

      return tiles;
    } catch (error) {
      console.error("❌ Error getting Last Added movies:", error);

      // Handle JSON parsing errors specifically
      if (error.message.includes("Unexpected token <") || error.message.includes("JSON")) {
        console.error("🚨 JSON parsing error in Last Added movies - API returned HTML");
      }

      return [];
    }
  };
}

// Export helper functions
export const clearLastAddedCache = () => {
  lastAddedCache.clear();
  console.log("🗑️ Last Added movies cache cleared");
};

export const clearLastAddedCacheForPlaylist = (baseUrl: string, username: string, password: string) => {
  const keysToDelete = Array.from(lastAddedCache.keys()).filter(key =>
    key.startsWith(`${baseUrl}_${username}_${password}_`),
  );

  keysToDelete.forEach(key => lastAddedCache.delete(key));
  console.log(`🗑️ Cleared ${keysToDelete.length} cache entries for playlist: ${baseUrl}`);
};

export const getLastAddedCacheStatus = () => {
  const status = Array.from(lastAddedCache.entries()).map(([key, value]) => ({
    key,
    itemCount: value.data.length,
    age: Date.now() - value.timestamp,
    ageMinutes: Math.floor((Date.now() - value.timestamp) / (1000 * 60)),
    isExpired: Date.now() - value.timestamp > CACHE_EXPIRY,
    version: value.version,
    isValidVersion: value.version === CACHE_VERSION,
  }));

  return status;
};

export const getLastAddedStats = () => {
  return {
    cacheSize: lastAddedCache.size,
    tilesSize: leftoverTiles.size,
    optimizationMethod: "First 5 Categories Only with Enhanced Error Handling",
    maxMoviesProcessed: "~500-2000 instead of 90,000+",
    performance: "Much faster loading with JSON error prevention",
    dnsSupport: "Enabled - uses custom DNS when configured",
    vpnSupport: "Enabled - handled by fetchWorkerManager",
    errorHandling: "Enhanced - prevents JSON parsing crashes",
  };
};
